import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Copy, ExternalLink, Power, Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { SubmissionsList } from "./SubmissionsList";

interface AssistedLink {
  id: string;
  token: string;
  expires_at: string;
  created_at: string;
  is_active: boolean;
  max_submissions: number | null;
  submission_count: number;
  notes: string | null;
  link_type: string;
}

interface AssistedLinkCardProps {
  link: AssistedLink;
  onUpdate: () => void;
}

export function AssistedLinkCard({ link, onUpdate }: AssistedLinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const expiresAt = new Date(link.expires_at);
  const now = new Date();
  const isExpired = now > expiresAt;
  const isLimitReached = link.max_submissions && link.submission_count >= link.max_submissions;

  const publicUrl = `${window.location.origin}/coleta/${link.token}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleToggleActive = async () => {
    setLoading(true);
    const { error } = await supabase
      .from('assisted_collection_links')
      .update({ is_active: !link.is_active })
      .eq('id', link.id);

    if (error) {
      toast.error("Erro ao atualizar link");
    } else {
      toast.success(link.is_active ? "Link desativado" : "Link reativado");
      onUpdate();
    }
    setLoading(false);
  };

  return (
    <Card className={!link.is_active || isExpired || isLimitReached ? "opacity-60" : ""}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={link.is_active && !isExpired && !isLimitReached ? "default" : "secondary"}>
                {!link.is_active ? "Desativado" : isExpired ? "Expirado" : isLimitReached ? "Limite Atingido" : "Ativo"}
              </Badge>
              {link.max_submissions && (
                <Badge variant="outline">
                  {link.submission_count}/{link.max_submissions} submissões
                </Badge>
              )}
              {!link.max_submissions && link.submission_count > 0 && (
                <Badge variant="outline">
                  {link.submission_count} submiss{link.submission_count === 1 ? 'ão' : 'ões'}
                </Badge>
              )}
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                Criado {formatDistanceToNow(new Date(link.created_at), {
                  addSuffix: true,
                  locale: ptBR
                })}
              </p>
              <p>
                Expira {formatDistanceToNow(expiresAt, {
                  addSuffix: true,
                  locale: ptBR
                })}
              </p>
              {link.notes && (
                <p className="text-foreground mt-2">
                  <span className="font-medium">Obs:</span> {link.notes}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm font-mono break-all">
              <span className="flex-1 truncate">{publicUrl}</span>
            </div>

            {link.submission_count > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="mt-2"
              >
                {expanded ? <ChevronUp className="h-4 w-4 mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
                {expanded ? 'Ocultar' : 'Ver'} Submissões ({link.submission_count})
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopy}
              disabled={!link.is_active || isExpired}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => window.open(publicUrl, '_blank')}
              disabled={!link.is_active || isExpired}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant={link.is_active ? "destructive" : "default"}
              size="icon"
              onClick={handleToggleActive}
              disabled={loading || isExpired}
            >
              <Power className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {expanded && <SubmissionsList linkId={link.id} />}
      </CardContent>
    </Card>
  );
}
